import { useEffect } from "react";
import { useNotificationPreferencesStore } from "@store/notificationPreferencesStore";
import type { InAppEventType } from "@store/inAppNotificationsStore";
import { useInAppNotificationsStore } from "@store/inAppNotificationsStore";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import type { OrdreStatus } from "types/ordre";
import type { NotifPrefValue } from "@services/api/notifications";
import { useDataStore } from "@store/user_org";
import { NotificationsAdminTable } from "./NotificationsAdminTable";

type EventConfig = {
  type: InAppEventType;
  title: string;
  description: string;
};

const EVENT_CONFIGS: EventConfig[] = [
  {
    type: "payment_received",
    title: "Paiement reçu",
    description: "Lorsqu'un client effectue un paiement (acompte ou solde)",
  },
  {
    type: "booking_created",
    title: "Nouvelle réservation",
    description: "Lorsqu'un client effectue une réservation en ligne",
  },
  {
    type: "devis_signed",
    title: "Devis signé",
    description: "Lorsqu'un client signe un devis",
  },
  {
    type: "document_signed",
    title: "Document signé",
    description: "Lorsqu'un compte rendu a été signé par toutes les parties",
  },
  {
    type: "ordre_status_changed",
    title: "Changement de statut",
    description: "Lorsqu'un ordre de réparation change de statut",
  },
  {
    type: "avis_received",
    title: "Avis client",
    description: "Lorsqu'un client laisse une note",
  },
  {
    type: "new_message",
    title: "Nouveau message",
    description: "Lorsqu'un message est reçu dans une conversation",
  },
  {
    type: "devis_auto_cancelled",
    title: "Devis annulé automatiquement",
    description: "Lorsqu'un devis est annulé automatiquement",
  },
];

const ORDRE_STATUS_OPTIONS: { value: OrdreStatus; label: string }[] = [
  { value: "a_preparer", label: "À préparer" },
  { value: "devis_initial", label: "Devis initial" },
  { value: "commande", label: "Commande" },
  { value: "pret", label: "Prêt" },
  { value: "reception", label: "Réceptionné" },
  { value: "en_cours", label: "En cours" },
  { value: "devis_supp", label: "Devis supp." },
  { value: "accord", label: "Accord" },
  { value: "acompte", label: "Acompte" },
  { value: "fin_de_travaux", label: "Fin de travaux" },
  { value: "facturation", label: "Facturation" },
  { value: "solde", label: "Solde" },
  { value: "solde_paye", label: "Soldé payé" },
  { value: "disponible", label: "Disponible" },
  { value: "restitue", label: "Restitué" },
  { value: "arrete", label: "Arrêté" },
];

const PREF_OPTIONS: { value: NotifPrefValue; label: string }[] = [
  { value: "always", label: "Toujours" },
  { value: "assigned", label: "Si assigné" },
  { value: "off", label: "Désactivé" },
];

function PrefSelector({
  value,
  onChange,
}: {
  value: NotifPrefValue;
  onChange: (v: NotifPrefValue) => void;
}) {
  return (
    <div className="flex rounded-lg border border-neutral-200 overflow-hidden">
      {PREF_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1 text-h3 transition-colors ${
            value === opt.value
              ? "bg-black text-white"
              : "bg-white text-shadow_50 hover:bg-neutral-50"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function NotificationsSettings() {
  const preferences = useNotificationPreferencesStore((s) => s.preferences);
  const setPreference = useNotificationPreferencesStore((s) => s.setPreference);
  const ordreStatusFilters = useNotificationPreferencesStore(
    (s) => s.ordreStatusFilters,
  );
  const setOrdreStatusFilter = useNotificationPreferencesStore(
    (s) => s.setOrdreStatusFilter,
  );
  const loading = useNotificationPreferencesStore((s) => s.loading);
  const fetchPreferences = useNotificationPreferencesStore(
    (s) => s.fetchPreferences,
  );
  const notifCount = useInAppNotificationsStore((s) => s.notifications.length);
  const clearAll = useInAppNotificationsStore((s) => s.clearAll);
  const userData = useDataStore((s) => s.userData);

  const isAdmin =
    userData?.config?.access_type === "OWNER" ||
    userData?.config?.access_type === "ADMIN";

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const allOrdreStatusesSelected = ORDRE_STATUS_OPTIONS.every((o) =>
    ordreStatusFilters.includes(o.value),
  );

  if (loading) {
    return (
      <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-700">
        <div className="flex flex-col gap-4">
          <Text1>Notifications</Text1>
          <Text2>Chargement des préférences...</Text2>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10">
      <div className="flex flex-col gap-4 max-w-700">
        <div className="flex flex-col">
          <Text1>Notifications</Text1>
          <Text2>Configuration des notifications visuelles.</Text2>
          <Text3>
            Choisissez pour chaque type de notification si vous souhaitez la
            recevoir toujours, uniquement pour les ordres qui vous sont assignés,
            ou jamais.
          </Text3>
        </div>

        <div className="flex flex-col gap-2">
          {EVENT_CONFIGS.map((config) => (
            <div key={config.type} className="flex flex-col">
              <div className="flex flex-row items-center justify-between bg-white p-3 rounded-xl shadow-sm border-1">
                <div className="flex flex-col mr-4">
                  <div className="font-medium text-black text-h3">
                    {config.title}
                  </div>
                  <div className="text-h3 text-shadow_50">
                    {config.description}
                  </div>
                </div>
                <PrefSelector
                  value={
                    preferences[config.type] === true as any ? "always"
                    : preferences[config.type] === false as any ? "off"
                    : preferences[config.type] ?? "always"
                  }
                  onChange={(v) => setPreference(config.type, v)}
                />
              </div>
              {config.type === "ordre_status_changed" &&
                preferences.ordre_status_changed !== "off" && (
                  <div className="ml-6 mt-1 mb-1 p-3 bg-white rounded-xl shadow-sm border-1">
                    <div className="flex flex-row items-center justify-between mb-2">
                      <div className="text-h3 text-shadow_50">
                        Filtrer par statut :
                      </div>
                      <button
                        onClick={() => {
                          const enable = !allOrdreStatusesSelected;
                          ORDRE_STATUS_OPTIONS.forEach((o) =>
                            setOrdreStatusFilter(o.value, enable),
                          );
                        }}
                        className="text-h3 text-blue-500 hover:text-blue-600 transition-colors"
                      >
                        {allOrdreStatusesSelected
                          ? "Tout désélectionner"
                          : "Tout sélectionner"}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {ORDRE_STATUS_OPTIONS.map((opt) => {
                        const active = ordreStatusFilters.includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            onClick={() =>
                              setOrdreStatusFilter(opt.value, !active)
                            }
                            className={`px-3 py-1 rounded-full text-h3 border transition-colors ${
                              active
                                ? "bg-black text-white border-black"
                                : "bg-white text-shadow_50 border-neutral-200 hover:border-neutral-400"
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
            </div>
          ))}
        </div>

        {/* Notification history */}
        <div className="flex flex-col gap-2 bg-white p-4 rounded-xl shadow-sm border-1">
          <div className="flex flex-row items-center justify-between">
            <div className="font-medium text-black text-h3">Historique</div>
            {notifCount > 0 && (
              <button
                onClick={clearAll}
                className="text-h3 text-red-500 hover:text-red-600 transition-colors"
              >
                Tout effacer ({notifCount})
              </button>
            )}
          </div>
          <div className="text-h3 text-shadow_50">
            {notifCount === 0
              ? "Aucune notification dans l'historique."
              : `${notifCount} notification${notifCount > 1 ? "s" : ""} dans l'historique.`}
          </div>
        </div>
      </div>

      {/* Admin table — outside max-w-700 constraint */}
      {isAdmin && <NotificationsAdminTable />}
    </div>
  );
}
